import {
	ApolloCache,
	DefaultContext,
	FetchResult,
	MutationFunctionOptions,
	OperationVariables,
} from '@apollo/client';
import { navigate } from 'gatsby';
import { DateTime } from 'luxon';
import React, { FC } from 'react';
import { toast } from 'react-toastify';

import BasicText from '@ui/atoms/BasicText';
import Button, { IButtonProps } from '@ui/atoms/Button';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import TextButton from '@ui/atoms/TextButton';
import Toast from '@ui/atoms/Toast';
import WYSIWYGContent from '@ui/atoms/WYSIWYGContent';

import MembershipCard from '@ui/organisms/MembershipCard';

import aligned from '@ui/styled/Aligned';
import fullWidth from '@ui/styled/FullWidth';

import { MembershipSubscription } from '@hooks/useMemberInfo';

import { interpolate } from '@utils/butter';

export interface ICancelationConfirmationPageContent {
	membershipHeader: string;
	membershipInfo: string;
	trialHeader: string;
	trialInfo: string;
	benefitsHeader: string;
	benefitsTopics: { info: string }[];
	keepTrialLabel: string;
	nevermindLabel: string;
	cancelLabel: string;
	supportCancel: string;
}

interface ICancelationConfirmationProps {
	cancelMembership: (
		options?:
			| MutationFunctionOptions<unknown, OperationVariables, DefaultContext, ApolloCache<unknown>>
			| undefined
	) => Promise<FetchResult<unknown>>;
	pageContent: ICancelationConfirmationPageContent;
	membershipSubscription?: MembershipSubscription;
	membershipWaived?: boolean;
}

const AlignedColumn = aligned<IFlexColumn>(fullWidth<IFlexColumn>(FlexColumn));
const FullWidthButton = fullWidth<IButtonProps>(Button);

const CancelationConfirmation: FC<ICancelationConfirmationProps> = ({
	cancelMembership,
	membershipSubscription,
	membershipWaived,
	pageContent,
}) => {
	const {
		membershipHeader,
		membershipInfo,
		trialHeader,
		trialInfo,
		benefitsHeader,
		benefitsTopics,
		keepTrialLabel,
		nevermindLabel,
		cancelLabel,
		supportCancel,
	} = pageContent;

	const isTrial = membershipSubscription?.isTrial;

	const remainingTrialLength = Math.ceil(
		DateTime.fromISO(membershipSubscription?.upcomingInvoice?.nextPaymentAttempt ?? '').diffNow(
			'days'
		).days
	);

	const membershipEndDate = DateTime.fromISO(
		membershipSubscription?.nextSubscriptionRenewalDate ?? ''
	).toLocaleString(DateTime.DATE_MED);

	const continueCancelation = async () => {
		if (!membershipWaived) {
			navigate('../cancel_winback');
			return;
		}

		await cancelMembership({
			variables: {
				authenticatedMember: true,
			},
		})
			.then(() => {
				navigate('../cancel_survey');
			})
			.catch(() => {
				toast.error(
					<WYSIWYGContent
						color="white"
						linkColor="white"
						dangerouslySetInnerHTML={{ __html: supportCancel }}
					/>
				);
			});
	};

	return (
		<>
			<Toast
				position="top-center"
				theme="colored"
				pauseOnHover
				pauseOnFocusLoss={false}
				autoClose={7500}
			/>
			<AlignedColumn gap="lg" alignItems="center">
				<FlexColumn gap="sm">
					<HeadingText>
						{isTrial ? interpolate(trialHeader, { remainingTrialLength }) : membershipHeader}
					</HeadingText>
					<BasicText>
						{isTrial ? trialInfo : interpolate(membershipInfo, { membershipEndDate })}
					</BasicText>
					<MembershipCard header={benefitsHeader} topics={benefitsTopics} />
				</FlexColumn>
				<FullWidthButton
					variant="secondary"
					type="button"
					label={isTrial ? keepTrialLabel : cancelLabel}
					onClick={() => (isTrial ? navigate('..') : continueCancelation())}
				/>
				<TextButton
					variant="text"
					underline
					label={isTrial ? cancelLabel : nevermindLabel}
					onClick={() => (isTrial ? continueCancelation() : navigate('..'))}
				/>
			</AlignedColumn>
		</>
	);
};

export default CancelationConfirmation;
